// SSE wire protocol for the chat backend.
//
// Source of truth: IFE `app/controllers/chat_controller.rb` and
// `app/lib/chat/sse_event_sink.rb`. Tool result payloads are defined in
// `app/lib/mcp/tools/*.rb`. Keep these types in sync when the backend changes.

export type SessionEventData = {
  session_id: string
  model_alias: string
  intention: string
  available_models: string[]
}

export type SessionMetaEventData = {
  model_alias: string
}

export type StatusEventData = {
  message: string
}

export type TokenEventData = {
  text: string
}

export type ToolStartEventData = {
  tool: string
  input: Record<string, unknown>
}

export type ToolDoneStatus = "success" | "error"

export type ToolDoneEventData = {
  tool: string
  status: ToolDoneStatus
  error?: string
  result?: unknown
}

export type UIActionKind = "select" | "dismiss"

export type UIActionDescriptor = {
  id: string
  kind: UIActionKind
  label: string
}

export type UIEventData = {
  id: string
  type: string
  data: Record<string, unknown>
  actions?: UIActionDescriptor[]
  fallback_text?: string
}

export type WarningEventData = {
  code: "approaching_token_cap"
  message: string
  usage: number
  cap: number
}

export type RecoverableErrorCode =
  | "turn_in_progress"
  | "missing_message"
  | "internal_error"
  | "bedrock_throttled"
  | "bedrock_timeout"
  | "bedrock_unavailable"
export type TerminalErrorCode = "session_full" | "token_cap_exceeded"
export type ErrorCode = RecoverableErrorCode | TerminalErrorCode

export type ErrorEventData = {
  code: ErrorCode | string
  message: string
}

export type DoneEventData = {
  usage: { input_tokens: number; output_tokens: number }
  cost_usd: number
  stop_reason: string
}

// Tool result payloads (subset — add more as needed).
// All tools use `{success: boolean, ...}` discriminated unions; see
// IFE `app/lib/mcp/tools/base.rb` (`structured_response` / `error_response`).

export type CreateAppletSuccessResult = {
  success: true
  applet_slug: string
  applet_url: string
}

export type ToolFailureResult = {
  success: false
  errors: Array<{ field: string; message: string }>
}

export type CreateAppletResult = CreateAppletSuccessResult | ToolFailureResult

export const isCreateAppletSuccessResult = (result: unknown): result is CreateAppletSuccessResult => {
  if (!result || typeof result !== "object") return false
  const r = result as Record<string, unknown>
  return r.success === true && typeof r.applet_slug === "string" && r.applet_slug.length > 0
}
